import { createContext, useCallback, useContext, useMemo } from 'react'
import {
  createSearchParams,
  useSearchParams,
  type SetURLSearchParams,
  type URLSearchParamsInit,
} from 'react-router-dom'

/**
 * DE QUEM e a Analise de metricas que esta na tela (MH9-02).
 *
 * A mesma tela tem dois leitores:
 *
 * - o PROPRIO aluno, em /app/analise-metricas (e /app/analise-metricas/:area) — o padrao,
 *   sem provider nenhum: nada muda para quem ja usava a tela;
 * - o PADRINHO, na aba "Análise de métricas" da ficha do afilhado
 *   (/instructor/students/:id), via `MenteeMetricsScope` (MenteeMetricsScope.tsx).
 *
 * O escopo decide tres coisas, e so tres:
 *
 * 1. a FONTE dos dados — os hooks de `useDashboard` leem `studentId` e trocam o endpoint
 *    do aluno pelo espelho em /api/v1/instructor/students/:id/metrics, com outra queryKey;
 * 2. o ESTADO DE URL — dentro da ficha, `?tab=` ja e a aba da ficha. Os parametros da
 *    tela (`tab`, `period`, `tema`, `especialidade`...) vao prefixados com `m.`, e
 *    `useMetricsSearchParams` devolve a mesma API do `useSearchParams` ja sem o prefixo;
 * 3. os LINKS — detalhe de area, volta para as abas e "Por Prova" ficam dentro da ficha;
 *    as acoes que so fazem sentido para o aluno (registrar estudo, meta TED, /app/provas,
 *    detalhe de prova, comparativo) somem (`isMentee`).
 */
export const METRICS_ROUTE = '/app/analise-metricas'

/** Prefixo dos parametros da tela quando ela vive dentro da ficha do afilhado. */
export const MENTEE_PARAM_PREFIX = 'm.'

/** Parametro (sem prefixo) que abre o detalhe de uma area dentro da ficha. */
export const MENTEE_AREA_PARAM = 'area'

export type HrefParams = Record<string, string | number | null | undefined>

export interface MetricsScope {
  /** `null` = o proprio aluno logado; numero = o afilhado, lido pelo padrinho. */
  studentId: number | null
  isMentee: boolean
  /** A tela de abas, com estes parametros (sem prefixo). */
  analysisHref: (params?: HrefParams) => string
  /** O detalhe de uma area (codigo em qualquer caixa), com estes parametros. */
  trackHref: (code: string, params?: HrefParams) => string
}

export function toQuery(params: HrefParams = {}, prefix = ''): URLSearchParams {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return
    query.set(`${prefix}${key}`, String(value))
  })
  return query
}

export function withQuery(path: string, query: URLSearchParams): string {
  const text = query.toString()
  return text ? `${path}?${text}` : path
}

const STUDENT_SCOPE: MetricsScope = {
  studentId: null,
  isMentee: false,
  analysisHref: (params) => withQuery(METRICS_ROUTE, toQuery(params)),
  trackHref: (code, params) =>
    withQuery(`${METRICS_ROUTE}/${encodeURIComponent(String(code).toLowerCase())}`, toQuery(params)),
}

/** O provider do afilhado mora em `MenteeMetricsScope.tsx` (fast refresh: so componentes la). */
export const MetricsScopeContext = createContext<MetricsScope>(STUDENT_SCOPE)

export function useMetricsScope(): MetricsScope {
  return useContext(MetricsScopeContext)
}

function stripPrefix(all: URLSearchParams): URLSearchParams {
  const scoped = new URLSearchParams()
  all.forEach((value, key) => {
    if (key.startsWith(MENTEE_PARAM_PREFIX)) scoped.append(key.slice(MENTEE_PARAM_PREFIX.length), value)
  })
  return scoped
}

/**
 * Drop-in do `useSearchParams` para a tela de metricas. Para o aluno, e o proprio
 * `useSearchParams`. Na ficha do afilhado, le e escreve so os parametros `m.*`,
 * preservando os da pagina (`?tab=` da ficha e qualquer outro).
 */
export function useMetricsSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const { isMentee } = useMetricsScope()
  const [all, setAll] = useSearchParams()

  const scoped = useMemo(() => (isMentee ? stripPrefix(all) : all), [isMentee, all])

  const setScoped = useCallback<SetURLSearchParams>(
    (nextInit, options) => {
      if (!isMentee) {
        setAll(nextInit, options)
        return
      }

      setAll((previous) => {
        const resolved: URLSearchParamsInit =
          typeof nextInit === 'function' ? nextInit(stripPrefix(previous)) : (nextInit ?? '')
        const next = new URLSearchParams()
        previous.forEach((value, key) => {
          if (!key.startsWith(MENTEE_PARAM_PREFIX)) next.append(key, value)
        })
        createSearchParams(resolved).forEach((value, key) => next.append(`${MENTEE_PARAM_PREFIX}${key}`, value))
        return next
      }, options)
    },
    [isMentee, setAll],
  )

  return [scoped, setScoped]
}

/**
 * O destino de uma acao que SO o aluno pode fazer (registrar estudo, ajustar a meta TED):
 * a rota para o aluno, `undefined` na ficha do afilhado. `EmptyStateCard` ja omite o botao
 * sem `ctaTo`; links condicionais testam o retorno.
 */
export function useStudentActionTo(route: string): string | undefined {
  const { isMentee } = useMetricsScope()
  return isMentee ? undefined : route
}
